import React from 'react'
import { makeStyles } from 'tss-react/mui'
import { keyframes } from 'tss-react'
import { IndicatorColor } from './types'
import { Theme } from '@mui/material'

interface StyleProps {
  inColor: IndicatorColor
  outColor: IndicatorColor
  reverse: boolean
}

/* Function that maps color string to a color from theme */
const getIndicatorColor = (theme: Theme, color: IndicatorColor): string => {
  switch (color) {
    case IndicatorColor.Green:
      return theme.palette.liveView.flowIndicatorInColor
    case IndicatorColor.Yellow:
      return theme.palette.liveView.flowIndicatorOutColor
  }
}

const useStyles = makeStyles<StyleProps>()((theme, props) => {
  const outColor = getIndicatorColor(theme, props.outColor)
  const inColor = getIndicatorColor(theme, props.inColor)

  return {
    indicator: {
      width: 0,
      height: 0,
      borderLeft: '10px solid transparent',
      borderRight: '10px solid transparent',
      borderBottom: `10px solid ${theme.palette.grey.grey06}`,
      marginBottom: theme.spacing(1),
      borderRadius: 5,
      transform: props.reverse ? 'rotate(180deg)' : '',
      '&:last-of-type': {
        marginBottom: 0,
      },
    },
    animate: {
      animation: keyframes`
        0% { border-bottom-color: ${theme.palette.grey.grey06}}
        20% { border-bottom-color: ${props.reverse ? outColor : inColor} }
        40% { border-bottom-color: ${theme.palette.grey.grey06} }
      `,
      animationDuration: '2s',
      animationIterationCount: 'infinite',
    },
  }
})

interface EnergyFlowIndicatorProps {
  animate?: boolean
  reverse?: boolean
  energyFlowIndicatorsCount?: number
  inColor: IndicatorColor
  outColor: IndicatorColor
}

/** delay in ms for the flow animation */
const DELAY = 900

const EnergyFlowIndicator: React.FC<EnergyFlowIndicatorProps> = ({ animate, reverse, inColor, outColor, energyFlowIndicatorsCount = 5 }) => {
  const { classes, cx } = useStyles({ inColor, outColor, reverse: reverse ?? false })
  const indicators: React.ReactNode[] = []
  for (let i = 0; i < energyFlowIndicatorsCount; i++) {
    const delay = Math.abs((reverse ? 0 : DELAY) - (DELAY / (energyFlowIndicatorsCount - 1)) * i)

    indicators.push(
      <div
        className={cx(classes.indicator, { [classes.animate]: animate })}
        style={{
          animationDelay: `${delay}ms`,
        }}
        key={'flow-indicator-' + i}
      />
    )
  }

  return <div>{indicators}</div>
}

export default EnergyFlowIndicator
